import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "@phosphor-icons/react";
import { aender, schick } from "../../api/client";
import type { Leistung, Projekt } from "../../api/types";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { datum } from "../../lib/format";
import { useLeistungen } from "./MaengelReiter";
import { useBeteiligte } from "./BeteiligteReiter";
import { Auswahl } from "../../components/Auswahl";

type Form = {
  bezeichnung: string; auftragnehmer_id: string; vertragsstrafe: boolean; maengelfrist_monate: number;
  fertigstellungspflege: boolean; beginn_soll: string; ende_soll: string; fertigmeldung_am: string;
};
const LEER: Form = {
  bezeichnung: "", auftragnehmer_id: "", vertragsstrafe: false, maengelfrist_monate: 48,
  fertigstellungspflege: false, beginn_soll: "", ende_soll: "", fertigmeldung_am: "",
};

/** Leistung = Vertrag mit einem AN. Hier hängen die VOB-Fristen (E11). */
export function LeistungenReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const liste = useLeistungen(projekt.id);
  const [bearbeiten, setBearbeiten] = useState<Leistung | "neu" | null>(null);
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Leistungen und Verträge</h2>
        <div className="luft" />
        {kann && <button className="knopf haupt" onClick={() => setBearbeiten("neu")}><Plus size={16} weight="bold" /> Leistung</button>}
      </div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.map((l) => (
          <button key={l.id} className="zeile" disabled={!kann} onClick={() => setBearbeiten(l)}>
            <span />
            <div className="haupt">
              <div>{l.bezeichnung}</div>
              <div className="mute">
                {l.an ?? "kein Auftragnehmer"}
                {l.abnahme_am
                  ? ` · abgenommen ${datum(l.abnahme_am)} · Mängelansprüche bis ${datum(l.maengelfrist_ende)}`
                  : l.fiktive_abnahme_am ? ` · Fertigmeldung ${datum(l.fertigmeldung_am)} → fiktive Abnahme ${datum(l.fiktive_abnahme_am)}` : ""}
              </div>
            </div>
            <span style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {l.vertragsstrafe && <span className="chip warn">Vertragsstrafe</span>}
              {l.fertigstellungspflege && <span className="chip">Fertigstellungspflege</span>}
              {l.maengel_offen > 0 && <span className="chip fehler">{l.maengel_offen} Mängel</span>}
              {l.abnahme_am && <span className="chip gut">abgenommen</span>}
            </span>
          </button>
        ))}
        {liste.data?.length === 0 && <div className="leer">Noch keine Leistung. Jede Abnahme gehört zu einer.</div>}
      </div>
      {bearbeiten && <LeistungDialog projekt={projekt} leistung={bearbeiten === "neu" ? null : bearbeiten} zu={() => setBearbeiten(null)} />}
    </section>
  );
}

function LeistungDialog({ projekt, leistung, zu }: { projekt: Projekt; leistung: Leistung | null; zu: () => void }) {
  const qc = useQueryClient();
  const beteiligte = useBeteiligte(projekt.id);
  const [f, setF] = useState<Form>(leistung ? {
    bezeichnung: leistung.bezeichnung, auftragnehmer_id: leistung.auftragnehmer_id ?? "",
    vertragsstrafe: leistung.vertragsstrafe, maengelfrist_monate: leistung.maengelfrist_monate,
    fertigstellungspflege: leistung.fertigstellungspflege, beginn_soll: leistung.beginn_soll ?? "",
    ende_soll: leistung.ende_soll ?? "", fertigmeldung_am: leistung.fertigmeldung_am ?? "",
  } : LEER);
  const speichern = useMutation({
    mutationFn: () => {
      const rumpf = {
        ...f, auftragnehmer_id: f.auftragnehmer_id || null, beginn_soll: f.beginn_soll || null,
        ende_soll: f.ende_soll || null, fertigmeldung_am: f.fertigmeldung_am || null,
      };
      return leistung
        ? aender(`/projekte/${projekt.id}/leistungen/${leistung.id}`, rumpf)
        : schick(`/projekte/${projekt.id}/leistungen`, rumpf);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leistungen", projekt.id] }); zu(); },
  });
  const an = beteiligte.data?.filter((b) => b.art === "auftragnehmer") ?? [];
  return (
    <Dialog offen zu={zu} titel={leistung ? "Leistung" : "Neue Leistung"} fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!f.bezeichnung.trim() || speichern.isPending} onClick={() => speichern.mutate()}>Speichern</button>
    </>}>
      <div className="raster">
        <Feld titel="Bezeichnung" breit>
          <input className="eingabe" autoFocus placeholder="Los 1 Garten- und Landschaftsbau" value={f.bezeichnung}
                 onChange={(e) => setF({ ...f, bezeichnung: e.target.value })} />
        </Feld>
        <Feld titel="Auftragnehmer" breit>
          <Auswahl value={f.auftragnehmer_id} onChange={(e) => setF({ ...f, auftragnehmer_id: e.target.value })}>
            <option value="">–</option>
            {an.map((b) => <option key={b.id} value={b.id}>{b.firma || b.name}</option>)}
          </Auswahl>
        </Feld>
        <Feld titel="Beginn (Soll)"><input type="date" className="eingabe" value={f.beginn_soll} onChange={(e) => setF({ ...f, beginn_soll: e.target.value })} /></Feld>
        <Feld titel="Fertigstellung (Soll)"><input type="date" className="eingabe" value={f.ende_soll} onChange={(e) => setF({ ...f, ende_soll: e.target.value })} /></Feld>
        <Feld titel="Mängelansprüche (Monate)">
          <input type="number" min={0} max={240} className="eingabe" value={f.maengelfrist_monate}
                 onChange={(e) => setF({ ...f, maengelfrist_monate: Number(e.target.value) })} />
        </Feld>
        <Feld titel="Schriftliche Fertigmeldung AN">
          <input type="date" className="eingabe" value={f.fertigmeldung_am} onChange={(e) => setF({ ...f, fertigmeldung_am: e.target.value })} />
        </Feld>
        <label className="haken breit"><input type="checkbox" checked={f.vertragsstrafe} onChange={(e) => setF({ ...f, vertragsstrafe: e.target.checked })} />
          Vertragsstrafe vereinbart (Vorbehalt wird bei der Abnahme vorbelegt)</label>
        <label className="haken breit"><input type="checkbox" checked={f.fertigstellungspflege} onChange={(e) => setF({ ...f, fertigstellungspflege: e.target.checked })} />
          Pflanz-/Rasenarbeiten: Abnahme nach Fertigstellungspflege</label>
      </div>
      <p className="mute">VOB/B § 13 Abs. 4: 48 Monate für Bauwerke, 24 für andere Werke – maßgeblich ist der Vertrag.</p>
      <Meldung fehler={speichern.error} />
    </Dialog>
  );
}
